import { api } from "./api";

export type SpecStatus = "pending" | "in_progress" | "answered" | "blocked";
export type SpecCategory = "file_formats" | "worked_examples" | "judgement" | "validation" | "fixtures";

export interface SpecFile {
  id: string;
  filename: string;
  byte_size: number;
  mime_type: string;
  uploaded_at: string;
}

export interface SpecItem {
  id: string;
  key: string;
  category: SpecCategory;
  question: string;
  context: string | null;
  position: number;
  status: SpecStatus;
  answer_text: string | null;
  updated_at: string | null;
  updated_by: { id: string; name: string; email: string } | null;
  files: SpecFile[];
}

export const specApi = {
  list: () => api.get<{ items: SpecItem[] }>("/spec"),
  patch: (key: string, body: { status?: SpecStatus; answer_text?: string | null }) =>
    api.patch<{ key: string; status: SpecStatus; answer_text: string | null; updated_at: string }>(
      `/spec/items/${key}`,
      body
    ),
  uploadFiles: async (key: string, files: File[]) => {
    const fd = new FormData();
    files.forEach((f) => fd.append("files", f));
    const tokenRaw = localStorage.getItem("clearbooks.auth");
    let token: string | null = null;
    try {
      token = tokenRaw ? (JSON.parse(tokenRaw).state.accessToken as string) : null;
    } catch {
      /* ignore */
    }
    const res = await fetch(`/clearbooks/api/spec/items/${key}/files`, {
      method: "POST",
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: fd,
    });
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new Error(body.error ?? `upload failed ${res.status}`);
    }
    return res.json() as Promise<{ files: Array<{ id: string; filename: string }> }>;
  },
  fileUrl: (id: string) => `/clearbooks/api/spec/files/${id}`,
  deleteFile: (id: string) => api.delete<void>(`/spec/files/${id}`),
};

export const SPEC_CATEGORY_LABELS: Record<SpecCategory, { label: string; sub: string }> = {
  file_formats: {
    label: "File formats",
    sub: "IRD/EPF/ETF prescribed file structures we cannot guess.",
  },
  worked_examples: {
    label: "Worked examples",
    sub: "Formulae plus real-number examples so we can verify our implementation.",
  },
  judgement: {
    label: "Judgement & interpretation",
    sub: "Calls only an SL CA can confidently make.",
  },
  validation: {
    label: "Validation passes",
    sub: "Review our drafts and red-line what's wrong. Defaults are working but unverified.",
  },
  fixtures: {
    label: "Fixtures",
    sub: "Anonymised real files Keytiqa already has on hand.",
  },
};

export const SPEC_STATUS_TONE: Record<SpecStatus, { label: string; bg: string; fg: string }> = {
  pending: { label: "Pending", bg: "var(--paper-2)", fg: "var(--ink-3)" },
  in_progress: { label: "In progress", bg: "var(--gold-soft)", fg: "#6E5320" },
  answered: { label: "Answered", bg: "var(--accent-soft)", fg: "var(--accent-deep)" },
  blocked: { label: "Blocked", bg: "var(--rose-soft)", fg: "var(--rose)" },
};
